import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller {
  static targets = ["column", "cardContainer", "card"];
  static values = {
    updateUrl: String,
  };

  connect() {
    if (this.cardContainerTargets.length === 0) {
      console.error("[Kanban] No card containers found! Check data-kanban-target='cardContainer' attributes.");
      return;
    }

    this.initializeSortables();
  }

  disconnect() {
    this.sortables?.forEach((s) => s.destroy());
  }

  // Stimulus calls this for every card present on connect AND for any card
  // swapped in later — e.g. the turbo_stream.replace that runs after a
  // successful drag. Binding here (instead of once in connect) keeps the
  // just-moved card clickable; otherwise its fresh DOM node had no click
  // handler and silently stopped opening until a full page reload.
  cardTargetConnected(card) {
    const onClick = (e) => this.handleCardClick(e, card);
    const onKeydown = (e) => this.handleCardKeydown(e, card);
    card._kanbanHandlers = { onClick, onKeydown };
    card.addEventListener("click", onClick);
    card.addEventListener("keydown", onKeydown);
  }

  cardTargetDisconnected(card) {
    const handlers = card._kanbanHandlers;
    if (!handlers) return;
    card.removeEventListener("click", handlers.onClick);
    card.removeEventListener("keydown", handlers.onKeydown);
    delete card._kanbanHandlers;
  }

  handleCardClick(event, card) {
    if (this.isInteractiveElement(event.target)) {
      return;
    }

    // Only navigate if we didn't just drag
    if (this.wasDragging) {
      this.wasDragging = false;
      return;
    }

    const url = card.dataset.url;
    if (url) {
      window.Turbo.visit(url);
    }
  }

  handleCardKeydown(event, card) {
    if (this.isInteractiveElement(event.target)) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const url = card.dataset.url;
    if (url) {
      event.preventDefault();
      window.Turbo.visit(url);
    }
  }

  isInteractiveElement(target) {
    return target.closest(
      "a, button, input, select, textarea, summary, details, form, [data-kanban-interactive='true']"
    );
  }

  initializeSortables() {
    this.sortables = [];
    this.wasDragging = false;

    // Whole-card drag on every board: a press-and-move starts a drag from
    // anywhere on the card, while a click with no movement still opens it
    // (on touch, a short press-and-hold starts the drag). This replaces the
    // deals board's old handle-only drag, where grabbing the card body just
    // opened the deal instead of moving it between stages.
    const gestureOptions = { delay: 200, delayOnTouchOnly: true, touchStartThreshold: 10 };

    this.cardContainerTargets.forEach((container) => {
      const lockedColumn = container.dataset.kanbanDropDisabled === "true";
      const sortable = Sortable.create(container, {
        group: {
          name: "kanban",
          pull: !lockedColumn,
          put: !lockedColumn,
        },
        animation: 150,
        draggable: ".kanban-card",
        ghostClass: "sortable-ghost",
        dragClass: "shadow-lg",
        chosenClass: "sortable-chosen",
        // Drive drag with synthetic mouse events instead of the native HTML5
        // drag-and-drop API. Native DnD was unreliable (a drag often started
        // no lifecycle at all, so the card never moved); the fallback path is
        // consistent across browsers and input devices. fallbackTolerance keeps
        // a plain click (no real movement) from being read as a drag, so
        // click-to-open still works.
        forceFallback: true,
        fallbackTolerance: 5,
        ...gestureOptions,
        onStart: (evt) => {
          this.wasDragging = true;
          // Lock touch-action during drag so card moves freely
          evt.item.style.touchAction = "none";
        },
        onEnd: this.handleDragEnd.bind(this),
      });
      this.sortables.push(sortable);
    });
  }

  async handleDragEnd(event) {
    const { item, to, from } = event;
    // Support both deals (data-deal-id) and sponsors (data-sponsor-id)
    const dealId = item.dataset.dealId;
    const sponsorId = item.dataset.sponsorId;
    const itemId = dealId || sponsorId;
    const isSponsor = !!sponsorId;
    const newStage = to.dataset.stage;
    const oldStage = from.dataset.stage;

    // Keep wasDragging true for a bit to prevent click navigation
    setTimeout(() => {
      this.wasDragging = false;
    }, 200);

    // If dropped in the same column, don't update
    if (newStage === oldStage) {
      return;
    }

    // Build the URL with actual item ID (supports both DEAL_ID and SPONSOR_ID)
    let url = this.updateUrlValue;
    url = url.replace("DEAL_ID", itemId);
    url = url.replace("SPONSOR_ID", itemId);

    // Add loading state to the card
    item.classList.add("opacity-75", "pointer-events-none");

    try {
      // Send both stage (for deals) and pipeline_status (for sponsors)
      const body = isSponsor
        ? { pipeline_status: newStage }
        : { stage: newStage };

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
          Accept: "text/vnd.turbo-stream.html, application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Revert the drag if failed
        this.revertDrag(event);
        this.showError("Failed to update stage");
      } else {
        // Process turbo-stream response to update the card with fresh data
        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text();
          window.Turbo.renderStreamMessage(html);
        }
        // Update column counts
        this.updateColumnCounts(oldStage, newStage, isSponsor);
      }
    } catch (error) {
      this.revertDrag(event);
      this.showError("Error updating stage");
      console.error("Error updating stage:", error);
    } finally {
      // Remove loading state and restore touch scrolling
      item.classList.remove("opacity-75", "pointer-events-none");
      item.style.touchAction = "";
    }
  }

  revertDrag(event) {
    const { item, from, oldIndex } = event;
    from.insertBefore(item, from.children[oldIndex]);
  }

  updateColumnCounts(oldStage, newStage, isSponsor = false) {
    const prefix = isSponsor ? "sponsor" : "deal";

    // Update old column count
    const oldCountEl = document.getElementById(`${prefix}-count-${oldStage}`);
    if (oldCountEl) {
      const oldCount = parseInt(oldCountEl.textContent, 10);
      oldCountEl.textContent = Math.max(0, oldCount - 1);
    }

    // Update new column count
    const newCountEl = document.getElementById(`${prefix}-count-${newStage}`);
    if (newCountEl) {
      const newCount = parseInt(newCountEl.textContent, 10);
      newCountEl.textContent = newCount + 1;
    }
  }

  showError(message) {
    // Toast controller listens for "toast-show" on window (not "toast:show"
    // on an element).  Matching the event name and target so errors actually
    // surface instead of silently disappearing.
    window.dispatchEvent(new CustomEvent("toast-show", {
      detail: { message, type: "error" },
    }));
  }
}
